/**
 * Settings Effects
 * NgRx effects for settings side effects and API integration
 */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { SettingsService } from '../services/settings.service';
import { NotificationService } from '@core/services/notification.service';
import { httpErrorMessage } from '@core/utils/http-error';
import { TeamMember } from '../models/settings.models';
import { AppState } from '@core/store';
import * as SettingsActions from './settings.actions';
import * as SettingsSelectors from './settings.selectors';

@Injectable()
export class SettingsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<AppState>);
  private settingsService = inject(SettingsService);
  private notificationService = inject(NotificationService);

  // User Profile Effects
  loadUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.loadUserProfile),
      switchMap(() =>
        this.settingsService.getUserProfile().pipe(
          map(profile => SettingsActions.loadUserProfileSuccess({ profile })),
          catchError(error => of(SettingsActions.loadUserProfileFailure({ 
            error: error.message || 'Failed to load user profile' 
          })))
        )
      )
    )
  );

  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateUserProfile),
      switchMap(({ request }) =>
        this.settingsService.updateUserProfile(request).pipe(
          map(response => SettingsActions.updateUserProfileSuccess({ profile: response.user })),
          catchError(error => of(SettingsActions.updateUserProfileFailure({
            error: error.message || 'Failed to update user profile'
          })))
        )
      )
    )
  );

  updateUserProfileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateUserProfileSuccess),
      tap(() => this.notificationService.showSuccess('Profile updated successfully'))
    ),
    { dispatch: false }
  );

  uploadAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.uploadAvatar),
      switchMap(({ file }) =>
        this.settingsService.uploadAvatar(file).pipe(
          mergeMap(response => [
            SettingsActions.uploadAvatarSuccess({ avatarUrl: response.avatar_url ?? null }),
            SettingsActions.loadUserProfile()
          ]),
          catchError(error => of(SettingsActions.uploadAvatarFailure({
            error: httpErrorMessage(error, 'Failed to upload avatar')
          })))
        )
      )
    )
  );

  uploadAvatarSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.uploadAvatarSuccess),
      tap(({ avatarUrl }) => {
        if (avatarUrl) {
          this.notificationService.showSuccess('Profile photo updated');
        } else {
          this.notificationService.showSuccess('Photo uploaded. Your preview is shown until a URL is available from the server.');
        }
      })
    ),
    { dispatch: false }
  );

  uploadAvatarFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.uploadAvatarFailure),
      tap(({ error }) => this.notificationService.showError(error))
    ),
    { dispatch: false }
  );

  requestPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.requestPasswordReset),
      switchMap(() =>
        this.settingsService.requestPasswordResetEmail().pipe(
          map(() => SettingsActions.requestPasswordResetSuccess()),
          catchError(error => of(SettingsActions.requestPasswordResetFailure({
            error: error.message || 'Failed to send password reset email'
          })))
        )
      )
    )
  );

  requestPasswordResetSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.requestPasswordResetSuccess),
      tap(() => this.notificationService.showSuccess(
        'Password reset email sent. Check your inbox and use the link to set a new password.'
      ))
    ),
    { dispatch: false }
  );

  enable2FA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.enable2FA),
      switchMap(({ request }) =>
        this.settingsService.enable2FA(request).pipe(
          map(() => SettingsActions.enable2FASuccess()),
          catchError(error => of(SettingsActions.enable2FAFailure({
            error: httpErrorMessage(error, 'Failed to enable 2FA')
          })))
        )
      )
    )
  );

  enable2FASuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.enable2FASuccess),
      tap(() => this.notificationService.showSuccess('Two-factor authentication enabled'))
    ),
    { dispatch: false }
  );

  enable2FAFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.enable2FAFailure),
      tap(({ error }) => this.notificationService.showError(error))
    ),
    { dispatch: false }
  );

  disable2FA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.disable2FA),
      switchMap(({ request }) =>
        this.settingsService.disable2FA(request).pipe(
          map(() => SettingsActions.disable2FASuccess()),
          catchError(error => of(SettingsActions.disable2FAFailure({
            error: httpErrorMessage(error, 'Failed to disable 2FA')
          })))
        )
      )
    )
  );

  disable2FASuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.disable2FASuccess),
      tap(() => this.notificationService.showSuccess('Two-factor authentication disabled'))
    ),
    { dispatch: false }
  );

  disable2FAFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.disable2FAFailure),
      tap(({ error }) => this.notificationService.showError(error))
    ),
    { dispatch: false }
  );

  // Organization Effects
  loadOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.loadOrganization),
      switchMap(() =>
        this.settingsService.getOrganization().pipe(
          map(organization => SettingsActions.loadOrganizationSuccess({ organization })),
          catchError(error => of(SettingsActions.loadOrganizationFailure({
            error: error.message || 'Failed to load organization'
          })))
        )
      )
    )
  );

  updateOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateOrganization),
      switchMap(({ request }) =>
        this.settingsService.updateOrganization(request).pipe(
          map(response => SettingsActions.updateOrganizationSuccess({ organization: response.organization })),
          catchError(error => of(SettingsActions.updateOrganizationFailure({
            error: error.message || 'Failed to update organization'
          })))
        )
      )
    )
  );

  updateOrganizationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateOrganizationSuccess),
      tap(() => this.notificationService.showSuccess('Organization updated successfully'))
    ),
    { dispatch: false }
  );

  uploadLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.uploadLogo),
      switchMap(({ file }) =>
        this.settingsService.uploadLogo(file).pipe(
          map(response => SettingsActions.uploadLogoSuccess({ logoUrl: response.logo_url })),
          catchError(error => of(SettingsActions.uploadLogoFailure({
            error: error.message || 'Failed to upload logo'
          })))
        )
      )
    )
  );

  uploadLogoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.uploadLogoSuccess),
      tap(() => this.notificationService.showSuccess('Logo uploaded successfully'))
    ),
    { dispatch: false }
  );

  // Team Management Effects
  loadTeamMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SettingsActions.loadTeamMembers,
        SettingsActions.setTeamMembersFilters,
        SettingsActions.setTeamMembersPage
      ),
      withLatestFrom(this.store.select(SettingsSelectors.selectTeamMembersQuery)),
      switchMap(([, query]) =>
        this.settingsService.getTeamMembers(query).pipe(
          map(response => SettingsActions.loadTeamMembersSuccess({
            members: response.members,
            total: response.total,
            page: response.page,
            pageSize: response.page_size
          })),
          catchError(error => of(SettingsActions.loadTeamMembersFailure({
            error: error.message || 'Failed to load team members'
          })))
        )
      )
    )
  );

  inviteTeamMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.inviteTeamMember),
      switchMap(({ request }) =>
        this.settingsService.inviteTeamMember(request).pipe(
          map(response => SettingsActions.inviteTeamMemberSuccess({ member: response.member })),
          catchError(error => of(SettingsActions.inviteTeamMemberFailure({
            error: httpErrorMessage(error, 'Failed to invite team member')
          })))
        )
      )
    )
  );

  inviteTeamMemberSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.inviteTeamMemberSuccess),
      switchMap(() => [SettingsActions.loadTeamMembers({})]),
      tap(() => this.notificationService.showSuccess('Team member invited successfully'))
    )
  );

  inviteTeamMemberFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.inviteTeamMemberFailure),
      tap(({ error }) => this.notificationService.showError(error || 'Failed to invite team member'))
    ),
    { dispatch: false }
  );

  updateTeamMemberRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateTeamMemberRole),
      switchMap(({ memberId, request }) =>
        this.settingsService.updateTeamMemberRole(memberId, request).pipe(
          map(response => SettingsActions.updateTeamMemberRoleSuccess({
            member: {
              _id: memberId,
              role: (response.new_role || request.role) as TeamMember['role']
            } as TeamMember
          })),
          catchError(error => of(SettingsActions.updateTeamMemberRoleFailure({
            error: httpErrorMessage(error, 'Failed to update member role')
          })))
        )
      )
    )
  );

  updateTeamMemberRoleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateTeamMemberRoleSuccess),
      switchMap(() => [
        SettingsActions.loadTeamMembers({})
      ]),
      tap(() => this.notificationService.showSuccess('Member role updated successfully'))
    )
  );

  updateTeamMemberRoleFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateTeamMemberRoleFailure),
      tap(({ error }) => this.notificationService.showError(error))
    ),
    { dispatch: false }
  );

  removeTeamMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.removeTeamMember),
      switchMap(({ memberId }) =>
        this.settingsService.removeTeamMember(memberId).pipe(
          map(response => SettingsActions.removeTeamMemberSuccess({ memberId: response.member_id })),
          catchError(error => of(SettingsActions.removeTeamMemberFailure({
            error: error.message || 'Failed to remove team member'
          })))
        )
      )
    )
  );

  removeTeamMemberSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.removeTeamMemberSuccess),
      tap(() => this.notificationService.showSuccess('Team member removed successfully'))
    ),
    { dispatch: false }
  );

  resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.resendInvitation),
      switchMap(({ memberId }) =>
        this.settingsService.resendInvitation(memberId).pipe(
          map(() => SettingsActions.resendInvitationSuccess()),
          catchError(error => of(SettingsActions.resendInvitationFailure({
            error: error.message || 'Failed to resend invitation'
          })))
        )
      )
    )
  );

  resendInvitationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.resendInvitationSuccess),
      tap(() => this.notificationService.showSuccess('Invitation resent successfully'))
    ),
    { dispatch: false }
  );

  // Notification Preferences Effects
  loadNotificationPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.loadNotificationPreferences),
      switchMap(() =>
        this.settingsService.getNotificationPreferences().pipe(
          map(preferences => SettingsActions.loadNotificationPreferencesSuccess({ preferences })),
          catchError(error => of(SettingsActions.loadNotificationPreferencesFailure({
            error: error.message || 'Failed to load notification preferences'
          })))
        )
      )
    )
  );

  updateNotificationPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateNotificationPreferences),
      switchMap(({ request }) =>
        this.settingsService.updateNotificationPreferences(request).pipe(
          map(preferences => SettingsActions.updateNotificationPreferencesSuccess({ preferences })),
          catchError(error => of(SettingsActions.updateNotificationPreferencesFailure({
            error: error.message || 'Failed to update notification preferences'
          })))
        )
      )
    )
  );

  updateNotificationPreferencesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateNotificationPreferencesSuccess),
      tap(() => this.notificationService.showSuccess('Notification preferences updated'))
    ),
    { dispatch: false }
  );

  connectSlack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.connectSlack),
      switchMap(({ code, state }) =>
        this.settingsService.connectSlack(code, state).pipe(
          map(preferences => SettingsActions.connectSlackSuccess({ preferences })),
          catchError(error => of(SettingsActions.connectSlackFailure({
            error: httpErrorMessage(error, 'Failed to connect Slack')
          })))
        )
      )
    )
  );

  connectSlackSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.connectSlackSuccess),
      tap(() => this.notificationService.showSuccess('Slack connected successfully'))
    ),
    { dispatch: false }
  );

  disconnectSlack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.disconnectSlack),
      switchMap(() =>
        this.settingsService.disconnectSlack().pipe(
          map(preferences => SettingsActions.disconnectSlackSuccess({ preferences })),
          catchError(error => of(SettingsActions.disconnectSlackFailure({
            error: httpErrorMessage(error, 'Failed to disconnect Slack')
          })))
        )
      )
    )
  );

  disconnectSlackSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.disconnectSlackSuccess),
      tap(() => this.notificationService.showSuccess('Slack disconnected successfully'))
    ),
    { dispatch: false }
  );

  connectSlackFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.connectSlackFailure),
      tap(({ error }) => this.notificationService.showError(error || 'Failed to connect Slack'))
    ),
    { dispatch: false }
  );

  disconnectSlackFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.disconnectSlackFailure),
      tap(({ error }) => this.notificationService.showError(error || 'Failed to disconnect Slack'))
    ),
    { dispatch: false }
  );

  updateOrganizationFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.updateOrganizationFailure),
      tap(({ error }) => this.notificationService.showError(error || 'Failed to update organization'))
    ),
    { dispatch: false }
  );

  uploadLogoFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingsActions.uploadLogoFailure),
      tap(({ error }) => this.notificationService.showError(error || 'Failed to upload logo'))
    ),
    { dispatch: false }
  );

  private sortTeamMembers(members: TeamMember[], sort?: string): TeamMember[] {
    if (!sort) {
      return members;
    }

    const descending = sort.startsWith('-');
    const field = descending ? sort.slice(1) : sort;
    if (field !== 'last_active') {
      return members;
    }

    return [...members].sort((a, b) => {
      const aTime = this.memberLastActiveTime(a);
      const bTime = this.memberLastActiveTime(b);
      return descending ? bTime - aTime : aTime - bTime;
    });
  }

  private memberLastActiveTime(member: TeamMember): number {
    const value = member.last_active || member.last_login_at;
    if (!value) {
      return 0;
    }
    return new Date(value).getTime();
  }
}

